"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown, ChevronRight, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/ui/logo";
import { UserProfile } from "@/components/user-profile";
import { AppConfig } from "@/config/app-config";
import { getMenuByTemplate, type MenuItem } from "@/config/menu-templates";
import { cn } from "@/lib/utils";

export interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onCollapsedChange }: SidebarProps) {
  const [isCollapsedInternal, setIsCollapsedInternal] = useState(false);
  
  // 상황에 따라 prop 또는 내부 상태 사용
  const isCollapsed = collapsed !== undefined ? collapsed : isCollapsedInternal;
  const setIsCollapsed = (value: boolean) => {
    if (onCollapsedChange) {
      onCollapsedChange(value);
    } else {
      setIsCollapsedInternal(value);
    }
  };

  const pathname = usePathname();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [overlayMenuId, setOverlayMenuId] = useState<string | null>(null);
  const [overlayTop, setOverlayTop] = useState<number>(0);
  const menuItems = getMenuByTemplate(AppConfig.menu.template);
  const overlayTimeout = useRef<NodeJS.Timeout | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const menuButtonRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // 현재 경로에 해당하는 메뉴 자동 확장
  React.useEffect(() => {
    const findParentMenus = (
      items: MenuItem[],
      targetPath: string
    ): string[] => {
      const parentIds: string[] = [];

      const searchInMenu = (menuItems: MenuItem[], path: string): boolean => {
        for (const item of menuItems) {
          if (
            item.href &&
            (path === item.href || path.startsWith(item.href + "/"))
          ) {
            return true;
          }
          if (item.children) {
            if (searchInMenu(item.children, path)) {
              parentIds.push(item.id);
              return true;
            }
          }
        }
        return false;
      };

      searchInMenu(items, targetPath);
      return parentIds;
    };

    const parentMenus = findParentMenus(menuItems, pathname);
    if (parentMenus.length > 0) {
      setExpandedItems(parentMenus);
    }
  }, [pathname, menuItems]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActive = (href?: string) => {
    if (!href) return false;
    if (pathname === href) return true;
    if (href !== "/" && pathname.startsWith(href)) return true;

    // 특별 케이스: ODT config elements 상세보기는 search 메뉴에서 파생됨
    if (
      href === "/odt-config/search" &&
      pathname.startsWith("/odt-config/elements/")
    ) {
      return true;
    }

    return false;
  };

  const isExpanded = (itemId: string) => expandedItems.includes(itemId);

  // 현재 활성화된 최종 하위 메뉴명 구하기
  function findActiveMenu(items: MenuItem[]): MenuItem | null {
    for (const item of items) {
      if (item.href && isActive(item.href)) return item;
      if (item.children) {
        const found = findActiveMenu(item.children);
        if (found) return found;
      }
    }
    return null;
  }

  const activeMenu = findActiveMenu(menuItems);
  // LNB 상단에는 항상 최종 선택된 메뉴명을 표시
  let lnbTitle = activeMenu ? activeMenu.title : "";

  // hydration mismatch 방지: mount 이후에만 렌더링
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const expanded = isExpanded(item.id);
    const active = isActive(item.href);
    const hasActiveChild =
      hasChildren && item.children?.some((child) => isActive(child.href));

    // 오버레이 메뉴 렌더링 (collapsed 상태에서만)
    const showOverlay =
      isCollapsed && depth === 0 && hasChildren && overlayMenuId === item.id;

    return (
      <div
        key={item.id}
        className="space-y-1 relative"
        ref={(el) => {
          if (el) menuButtonRefs.current[item.id] = el;
        }}
      >
        <div
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors group",
            "hover:bg-accent hover:text-accent-foreground",
            (active || hasActiveChild) &&
              "bg-accent text-accent-foreground font-medium",
            depth > 0 && "ml-4",
            isCollapsed && depth === 0 && "justify-center px-2"
          )}
          onMouseEnter={(e) => {
            if (isCollapsed && hasChildren && depth === 0) {
              if (overlayTimeout.current) clearTimeout(overlayTimeout.current);
              setOverlayMenuId(item.id);
              // 오버레이 top 위치 계산
              const rect =
                menuButtonRefs.current[item.id]?.getBoundingClientRect();
              const sidebarRect = sidebarRef.current?.getBoundingClientRect();
              if (rect && sidebarRect) {
                setOverlayTop(rect.top - sidebarRect.top);
              } else if (rect) {
                setOverlayTop(rect.top);
              }
            }
          }}
          onMouseLeave={() => {
            if (isCollapsed && hasChildren && depth === 0) {
              overlayTimeout.current = setTimeout(
                () => setOverlayMenuId(null),
                150
              );
            }
          }}
        >
          {hasChildren ? (
            <button
              type="button"
              tabIndex={-1}
              className="flex items-center gap-3 flex-1 text-left bg-transparent border-none outline-none p-0 m-0"
              style={{ boxShadow: "none" }}
              onClick={() => {
                if (!isCollapsed) toggleExpanded(item.id);
              }}
            >
              <item.icon
                className={cn(
                  "w-4 h-4 flex-shrink-0",
                  isCollapsed && depth === 0 && "w-5 h-5"
                )}
              />
              {(!isCollapsed || depth > 0) && (
                <>
                  <span className="flex-1 min-w-0 truncate">{item.title}</span>
                  {item.badge && (
                    <Badge
                      variant="secondary"
                      className="text-xs flex-shrink-0"
                    >
                      {item.badge}
                    </Badge>
                  )}
                  <ChevronRight
                    className={cn(
                      "w-4 h-4 transition-transform flex-shrink-0",
                      expanded && "rotate-90"
                    )}
                  />
                </>
              )}
            </button>
          ) : (
            <Link
              href={item.href || "#"}
              className="flex items-center gap-3 flex-1"
            >
              <item.icon
                className={cn(
                  "w-4 h-4 flex-shrink-0",
                  isCollapsed && depth === 0 && "w-5 h-5"
                )}
              />
              {(!isCollapsed || depth > 0) && (
                <>
                  <span className="flex-1 min-w-0 truncate">{item.title}</span>
                  {item.badge && (
                    <Badge
                      variant="secondary"
                      className="text-xs flex-shrink-0"
                    >
                      {item.badge}
                    </Badge>
                  )}
                  {item.isNew && (
                    <Badge variant="default" className="text-xs flex-shrink-0">
                      NEW
                    </Badge>
                  )}
                  {item.isBeta && (
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      BETA
                    </Badge>
                  )}
                </>
              )}
            </Link>
          )}
        </div>
        {/* 오버레이 하위 메뉴 */}
        {showOverlay && (
          <div
            className="fixed z-[9999] min-w-[180px] bg-popover text-popover-foreground border border-border rounded-lg shadow-lg py-2"
            style={{
              left: sidebarRef.current
                ? sidebarRef.current.getBoundingClientRect().right
                : 64,
              top: overlayTop,
            }}
            onMouseEnter={() => {
              if (overlayTimeout.current) clearTimeout(overlayTimeout.current);
            }}
            onMouseLeave={() => {
              overlayTimeout.current = setTimeout(
                () => setOverlayMenuId(null),
                150
              );
            }}
          >
            {item.children?.map((child) => (
              <Link
                key={child.id}
                href={child.href || "#"}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors rounded-md",
                  isActive(child.href) &&
                    "bg-accent text-accent-foreground font-medium"
                )}
                onClick={() => setOverlayMenuId(null)}
              >
                <child.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{child.title}</span>
                {child.badge && (
                  <Badge variant="secondary" className="text-xs flex-shrink-0">
                    {child.badge}
                  </Badge>
                )}
                {child.isNew && (
                  <Badge variant="default" className="text-xs flex-shrink-0">
                    NEW
                  </Badge>
                )}
                {child.isBeta && (
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    BETA
                  </Badge>
                )}
              </Link>
            ))}
          </div>
        )}
        {/* 기존 하위 메뉴 렌더링 */}
        {hasChildren && expanded && (!isCollapsed || depth > 0) && (
          <div className="space-y-1">
            {item.children?.map((child) => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={sidebarRef}
      className={cn(
        "bg-background border-r border-border transition-all duration-300 flex flex-col relative h-screen",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* LNB 맨 위: 사용자 프로필 + X(닫기) 버튼, 접히면 햄버거 아이콘 */}
      <div
        className={cn(
          "flex items-center border-b border-border h-16 flex-shrink-0 overflow-hidden",
          "border-r border-border",
          isCollapsed ? "pb-1" : ""
        )}
      >
        {isCollapsed ? (
          <div className="w-full flex flex-col items-center justify-center h-full pt-1">
            <UserProfile isCollapsed={true} className="border-none py-0" />
            <button
              type="button"
              className="w-7 h-7 mx-auto p-1 rounded hover:bg-accent focus:outline-none focus:ring-0 mt-0.5 flex items-center justify-center"
              onClick={() => setIsCollapsed(false)}
              aria-label="사이드바 펼치기"
            >
              {/* 햄버거 아이콘 */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="4"
                  y="6"
                  width="16"
                  height="2"
                  rx="1"
                  fill="currentColor"
                />
                <rect
                  x="4"
                  y="11"
                  width="16"
                  height="2"
                  rx="1"
                  fill="currentColor"
                />
                <rect
                  x="4"
                  y="16"
                  width="16"
                  height="2"
                  rx="1"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex justify-between items-center w-full">
            <UserProfile
              isCollapsed={false}
              className="border-none py-0 flex-1"
            />
            <button
              type="button"
              className="ml-2 p-1 rounded hover:bg-accent focus:outline-none focus:ring-0"
              onClick={() => setIsCollapsed(true)}
              aria-label="사이드바 접기"
            >
              {/* X 아이콘 */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
      {/* 메뉴 영역 (스크롤 가능) */}
      <nav className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {menuItems.map((item) => renderMenuItem(item))}
        </div>
      </nav>
      {/* Footer (환경/버전 등) - absolute positioning */}
      {!isCollapsed && AppConfig.deployment.environment === "development" && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-background">
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="flex items-center justify-between">
              <span>환경</span>
              <Badge variant="outline" className="text-xs">
                {AppConfig.deployment.environment}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>템플릿</span>
              <Badge variant="secondary" className="text-xs capitalize">
                {AppConfig.menu.template}
              </Badge>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
